import {
  Button,
  ButtonIntent,
  ButtonTier,
  FormGroup,
  Input,
  Label,
  Select,
} from 'shared-components';
import { sortOptions } from '../selector-editor-utils';
import type { SelectorTemplateDraft } from '../use-selector-template-draft';
import styles from './canvas-inspector.module.css';

interface CanvasInspectorProps {
  state: SelectorTemplateDraft;
  selectedNodeId: string | null;
  selectedProductNodeId: string | null;
}

export const CanvasInspector = ({
  state,
  selectedNodeId,
  selectedProductNodeId,
}: CanvasInspectorProps) => {
  const {
    draft,
    nodes,
    productsById,
    uncoveredProducts,
    validation,
    updateDraft,
    updateNode,
    updateOption,
    handleAddOption,
    handleRemoveNode,
    handleRemoveOption,
  } = state;

  if (!draft) return null;

  // Product leaf selected
  if (selectedProductNodeId) {
    const productId = selectedProductNodeId.replace('product_', '');
    const product = productsById.get(productId);

    return (
      <div className={styles.inspector}>
        <div className={styles.sectionTitle}>Product</div>
        {product ? (
          <div className={styles.productInfo}>
            <div className={styles.productName}>{product.name}</div>
            <div className={styles.productPrice}>
              {(product.price / 100).toLocaleString(undefined, {
                style: 'currency',
                currency: 'USD',
              })}
            </div>
            {product.description && (
              <div className={styles.productDescription}>
                {product.description}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.missing}>Product not found</div>
        )}
      </div>
    );
  }

  // Question node selected
  if (selectedNodeId && draft.tree.nodes[selectedNodeId]) {
    const node = draft.tree.nodes[selectedNodeId];
    const options = sortOptions(node.options);
    const isRoot = selectedNodeId === draft.tree.rootNodeId;

    return (
      <div className={styles.inspector}>
        <div className={styles.sectionTitle}>Question</div>

        <FormGroup>
          <Label htmlFor="inspector-prompt">Prompt</Label>
          <Input
            id="inspector-prompt"
            value={node.prompt}
            onChange={(e) => {
              const value = e.target.value;
              updateNode(selectedNodeId, (n) => ({
                ...n,
                prompt: value,
              }));
            }}
            disabled={draft.isArchived}
          />
        </FormGroup>

        {!isRoot && (
          <Button
            tier={ButtonTier.Tertiary}
            onClick={() => {
              updateDraft((current) => ({
                ...current,
                tree: {
                  ...current.tree,
                  rootNodeId: selectedNodeId,
                },
              }));
            }}
            disabled={draft.isArchived}
            className={styles.setRootButton}
          >
            Set as Start
          </Button>
        )}

        <div className={styles.optionsTitle}>
          <span>Options</span>
          <Button
            tier={ButtonTier.Secondary}
            onClick={() => handleAddOption(selectedNodeId)}
            disabled={draft.isArchived}
          >
            Add
          </Button>
        </div>

        {options.map((option) => {
          let connectionLabel = 'Not connected';
          if (option.next.type === 'product' && option.next.productId) {
            const product = productsById.get(option.next.productId);
            connectionLabel = product
              ? `→ ${product.name}`
              : `→ Missing product`;
          } else if (option.next.type === 'node' && option.next.nodeId) {
            const targetNode = draft.tree.nodes[option.next.nodeId];
            connectionLabel = targetNode
              ? `→ ${targetNode.prompt || 'Untitled question'}`
              : `→ Missing question`;
          }

          return (
            <div key={option.id} className={styles.optionCard}>
              <Input
                value={option.label}
                onChange={(e) => {
                  const value = e.target.value;
                  updateOption(
                    selectedNodeId,
                    option.id,
                    (o) => ({ ...o, label: value })
                  );
                }}
                disabled={draft.isArchived}
                placeholder="Option label"
              />
              <div className={styles.optionConnection}>
                {connectionLabel}
              </div>
              <Button
                tier={ButtonTier.Tertiary}
                intent={ButtonIntent.Negative}
                onClick={() =>
                  handleRemoveOption(selectedNodeId, option.id)
                }
                disabled={draft.isArchived}
              >
                Remove
              </Button>
            </div>
          );
        })}

        <div className={styles.dangerZone}>
          <Button
            tier={ButtonTier.Tertiary}
            intent={ButtonIntent.Negative}
            onClick={() => handleRemoveNode(selectedNodeId)}
            disabled={draft.isArchived}
          >
            Remove Question
          </Button>
        </div>
      </div>
    );
  }

  // Nothing selected — show template info + validation
  return (
    <div className={styles.inspector}>
      <div className={styles.sectionTitle}>Template</div>

      <FormGroup>
        <Label htmlFor="inspector-root">Starting Question</Label>
        <Select
          id="inspector-root"
          value={draft.tree.rootNodeId}
          onChange={(e) => {
            const value = e.target.value;
            updateDraft((current) => ({
              ...current,
              tree: { ...current.tree, rootNodeId: value },
            }));
          }}
          disabled={draft.isArchived}
        >
          {nodes.map((n) => (
            <option key={n.id} value={n.id}>
              {n.prompt || 'Untitled question'} ({n.id})
            </option>
          ))}
        </Select>
      </FormGroup>

      <div className={styles.divider} />

      <div className={styles.sectionTitle}>Validation</div>
      <div className={styles.validationSummary}>
        {validation.valid ? '✅ Valid' : '⚠️ Needs attention'}
      </div>
      {validation.errors.length > 0 && (
        <ul className={styles.validationList}>
          {validation.errors.map((error, i) => (
            <li key={`e-${i}`} className={styles.errorItem}>
              {error.message}
            </li>
          ))}
        </ul>
      )}
      {validation.warnings.length > 0 && (
        <ul className={styles.validationList}>
          {validation.warnings.map((warning, i) => (
            <li key={`w-${i}`} className={styles.warningItem}>
              {warning.message}
            </li>
          ))}
        </ul>
      )}

      {uncoveredProducts.length > 0 && (
        <>
          <div className={styles.divider} />
          <div className={styles.sectionTitle}>Unassigned Products</div>
          <div className={styles.draggableProductList}>
            {uncoveredProducts.map((p) => (
              <div
                key={p._id.toString()}
                className={styles.draggableProduct}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(
                    'application/product-id',
                    p._id.toString()
                  );
                  e.dataTransfer.effectAllowed = 'move';
                }}
              >
                {p.name}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
